import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PdfReportPreviewModal from "../PdfReportPreviewModal";
import { showAlert, confirmAlert } from "../AppAlert/appAlert";
import {
  cancelActiveReportGeneration,
  clearReportGenerationAbort,
  registerReportGenerationAbort,
  setReportGenerationInProgress,
} from "../../utils/reportGenerationGuard";
import {
  generateAiProjectReportPdf,
  isReportGenerationRequestAborted,
} from "../../services/aiReportApi";
import { addOrUpdateReport } from "../../services/smeApi";
import { fetchReports } from "../../services/reportsApi";
import {
  getCompanyName,
  getProjectId,
  type SmePendingProject,
} from "../../utils/smeProjectUtils";
import ReferenceCompanyTickerModal, {
  type CompanyReportSelection,
} from "./ReferenceCompanyTickerModal";
import ReportCustomizeModal, {
  canCustomizeSavedReport,
  customizeBlockedMessage,
} from "./ReportCustomizeModal";
import SmeProjectReportHistory from "./SmeProjectReportHistory";
import { Alert, Button, Spinner, Textarea } from "../ui";

export type SmeProjectReportPanelProps = {
  project: SmePendingProject | null;
  clientUserId: number | null;
  onGeneratingChange?: (generating: boolean) => void;
  onCustomizingChange?: (customizing: boolean) => void;
};

const normalizeReportStatus = (value) =>
  String(value || "submitted").trim().toLowerCase();

const isSavedReportStatus = (value) => {
  const status = normalizeReportStatus(value);
  return status === "edit" || status === "upload";
};

const extractSavedReportItemId = (result) => {
  const payload = result?.data ?? result;
  const items = payload?.items ?? payload?.Items;
  if (!Array.isArray(items) || items.length === 0) return null;
  const first = items[0];
  return first?.reportItemId ?? first?.ReportItemId ?? null;
};

export default function SmeProjectReportPanel({
  project,
  clientUserId,
  onGeneratingChange,
  onCustomizingChange,
}: SmeProjectReportPanelProps) {
  const [remarks, setRemarks] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [isAiGeneratedAttachment, setIsAiGeneratedAttachment] = useState(false);
  const [activeDraftReportItemId, setActiveDraftReportItemId] = useState(null);
  const [pendingSourceHtmlPath, setPendingSourceHtmlPath] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const fileInputRef = useRef(null);

  const [isGeneratingProjectReport, setIsGeneratingProjectReport] = useState(false);
  const [generateRemainingSec, setGenerateRemainingSec] = useState(600);
  const [submittedReports, setSubmittedReports] = useState([]);
  const [localPdfPreview, setLocalPdfPreview] = useState(null);
  const [reportListReloadToken, setReportListReloadToken] = useState(0);
  const remarksInputRef = useRef(null);
  const [showTickerModal, setShowTickerModal] = useState(false);
  const [companyReportSelection, setCompanyReportSelection] =
    useState<CompanyReportSelection | null>(null);
  const [customizeTarget, setCustomizeTarget] = useState(null);

  const projectId = getProjectId(project);
  const companyNameForSearch = getCompanyName(project) || "";

  const savedReports = useMemo(
    () => submittedReports.filter((report) => isSavedReportStatus(report.status)),
    [submittedReports]
  );

  const extractFileName = (filePath) => {
    if (!filePath) return "report.pdf";
    const normalized = String(filePath).replace(/\\/g, "/");
    const parts = normalized.split("/");
    return parts[parts.length - 1] || "report.pdf";
  };

  const loadSubmittedReports = useCallback(async () => {
    if (!projectId) {
      setSubmittedReports([]);
      return;
    }

    try {
      const data = await fetchReports(projectId);
      setSubmittedReports(Array.isArray(data) ? data : []);
      setReportListReloadToken((token) => token + 1);
    } catch (error) {
      console.error("Error loading submitted reports:", error);
      setSubmittedReports([]);
    }
  }, [projectId]);

  useEffect(() => {
    loadSubmittedReports();
  }, [loadSubmittedReports]);

  useEffect(() => {
    onGeneratingChange?.(isGeneratingProjectReport);
    setReportGenerationInProgress(isGeneratingProjectReport);
    return () => {
      onGeneratingChange?.(false);
      setReportGenerationInProgress(false);
    };
  }, [isGeneratingProjectReport, onGeneratingChange]);

  useEffect(() => {
    onCustomizingChange?.(Boolean(customizeTarget));
    return () => onCustomizingChange?.(false);
  }, [customizeTarget, onCustomizingChange]);

  useEffect(() => {
    if (!isGeneratingProjectReport) {
      setGenerateRemainingSec(600);
      return undefined;
    }

    const ESTIMATED_SECONDS = 600;
    setGenerateRemainingSec(ESTIMATED_SECONDS);
    const startedAt = Date.now();
    const timerId = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setGenerateRemainingSec(Math.max(0, ESTIMATED_SECONDS - elapsed));
    }, 1000);

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const onPageHide = () => {
      cancelActiveReportGeneration();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.clearInterval(timerId);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [isGeneratingProjectReport]);

  const formatCountdown = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const generationStatusHint =
    generateRemainingSec > 540
      ? "Collecting assessment data and starting AI generation…"
      : generateRemainingSec > 300
        ? "AI is drafting the report. This can take up to 10 minutes…"
        : generateRemainingSec > 120
          ? "Still working — stay on this page…"
          : generateRemainingSec > 0
            ? "Wrapping up — converting the report to PDF…"
            : "Taking a little longer than usual. Stay on this page until it finishes…";

  const syncFileInput = (file) => {
    if (!fileInputRef.current) return;

    if (!file) {
      fileInputRef.current.value = "";
      return;
    }

    try {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
    } catch {
      // Some browsers block programmatic FileList assignment; value clear is still safe.
      fileInputRef.current.value = "";
    }
  };

  const handleRemarksChange = (e) => {
    setRemarks(e.target.value);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) {
      setSelectedFiles([]);
      return;
    }

    // Backend stores a single AttachmentURL per report.
    const file = files[0];
    const allowed =
      file.type === "application/pdf" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      /\.(pdf|docx)$/i.test(file.name);
    if (!allowed) {
      setSubmitError("Only PDF or Word (.docx) files are allowed.");
      e.target.value = "";
      return;
    }

    setSubmitError("");
    setIsAiGeneratedAttachment(false);
    setPendingSourceHtmlPath(null);
    setSelectedFiles([file]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setIsAiGeneratedAttachment(false);
    if (selectedFiles.length <= 1) {
      syncFileInput(null);
    }
  };

  const handleSaveReport = async () => {
    if (!project) {
      setSubmitError("No project selected");
      return;
    }

    if (!projectId) {
      setSubmitError("Project ID is missing");
      return;
    }

    if (!clientUserId) {
      setSubmitError("Client user is missing for this project");
      return;
    }

    const hasFile = selectedFiles.length > 0;
    if (!hasFile && !activeDraftReportItemId) {
      const message = "Attach a PDF (generate or upload) before saving.";
      setSubmitError(message);
      setSubmitMessage("");
      showAlert({
        type: "warning",
        title: "PDF required",
        message,
      });
      return;
    }

    const draftStatus = isAiGeneratedAttachment ? "edit" : "upload";

    const replaceTargetId =
      activeDraftReportItemId ||
      savedReports.find((report) => isSavedReportStatus(report.status))
        ?.reportItemId ||
      null;

    let replaceDraft = false;
    if (replaceTargetId) {
      replaceDraft = await confirmAlert({
        type: "warning",
        title: "Save draft",
        message: `Replace saved draft "${replaceTargetId}"? Choose Replace draft to overwrite it, or Save as new to create another version.`,
        confirmText: "Replace draft",
        cancelText: "Save as new",
      });
    }

    setIsSavingReport(true);
    setSubmitError("");
    setSubmitMessage("");

    try {
      const result = await addOrUpdateReport(
        projectId,
        clientUserId,
        remarks.trim(),
        selectedFiles,
        {
          status: draftStatus,
          reportItemId: replaceDraft ? replaceTargetId : null,
          replaceDraft,
          sourceHtmlPath: pendingSourceHtmlPath,
        }
      );

      const savedId = extractSavedReportItemId(result);
      if (savedId) {
        setActiveDraftReportItemId(savedId);
      } else if (!replaceDraft) {
        setActiveDraftReportItemId(null);
      }

      setPendingSourceHtmlPath(null);

      setSubmitMessage(
        replaceDraft
          ? "Draft replaced in place."
          : draftStatus === "edit"
            ? "AI report saved for editing. Submit when you are ready to send it."
            : "Uploaded report saved. Submit when you are ready to send it."
      );
      await loadSubmittedReports();
    } catch (error) {
      console.error("Error saving report:", error);
      setSubmitError(`Failed to save report: ${error.message}`);
    } finally {
      setIsSavingReport(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!project) {
      setSubmitError("No project selected");
      return;
    }

    if (!projectId) {
      setSubmitError("Project ID is missing");
      return;
    }

    if (!clientUserId) {
      setSubmitError("Client user is missing for this project");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitMessage("");

    try {
      const result = await addOrUpdateReport(
        projectId,
        clientUserId,
        remarks.trim(),
        selectedFiles,
        {
          status: "submitted",
          copyFromReportItemId:
            selectedFiles.length === 0 && activeDraftReportItemId
              ? activeDraftReportItemId
              : null,
          sourceHtmlPath: pendingSourceHtmlPath,
        }
      );

      console.log("Report submitted successfully:", result);
      setSubmitMessage("Report submitted successfully!");

      setRemarks("");
      setSelectedFiles([]);
      setIsAiGeneratedAttachment(false);
      setActiveDraftReportItemId(null);
      setPendingSourceHtmlPath(null);
      syncFileInput(null);
      await loadSubmittedReports();
    } catch (error) {
      console.error("Error submitting report:", error);
      setSubmitError(`Failed to submit report: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearForm = () => {
    setRemarks("");
    setSelectedFiles([]);
    setSubmitMessage("");
    setSubmitError("");
    setIsAiGeneratedAttachment(false);
    setActiveDraftReportItemId(null);
    setPendingSourceHtmlPath(null);
    syncFileInput(null);
  };

  const handleGenerateProjectReport = async (
    selection: CompanyReportSelection | null = companyReportSelection,
    editable = false
  ) => {
    if (!project) {
      setSubmitError("No project selected");
      return;
    }

    if (!projectId) {
      setSubmitError("Project id is required to generate the project report.");
      return;
    }

    setIsGeneratingProjectReport(true);
    setSubmitError("");
    setSubmitMessage("");

    const abortController = new AbortController();
    registerReportGenerationAbort(abortController);

    try {
      const { blob, fileName, sourceHtmlPath } = await generateAiProjectReportPdf({
        projectId,
        editable,
        signal: abortController.signal,
        marketIntelligence: selection
          ? {
              ticker: selection.ticker,
              includeGeneralUseCases: selection.includeGeneralUseCases,
              includeCompetitorUseCases: selection.includeCompetitorUseCases,
              competitorTiers: selection.competitorTiers,
              selectedCompetitors: selection.selectedCompetitors,
              includeComparableEngagements: selection.includeComparableEngagements,
            }
          : undefined,
      });

      const resolvedFileName = fileName || (editable ? "project-report.docx" : "project-report.pdf");
      const isDocx = /\.docx$/i.test(resolvedFileName);
      const reportFile = new File([blob], resolvedFileName, {
        type: isDocx
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : "application/pdf",
      });

      // Attach to the report form so Submit Report uploads it to SMEReports.
      setSelectedFiles([reportFile]);
      setIsAiGeneratedAttachment(true);
      setPendingSourceHtmlPath(sourceHtmlPath || null);
      syncFileInput(reportFile);

      setSubmitMessage(
        "AI report generated. Save it for editing or submit it with your feedback when ready."
      );

      // End generating state before download/success UI so leave-guards don't fire.
      setIsGeneratingProjectReport(false);
      setReportGenerationInProgress(false);
      onGeneratingChange?.(false);

      // Also download a local copy for the SME.
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showAlert({
        type: "success",
        title: "AI report generated",
        message:
          "Your report is ready. You can preview it, download it, and submit it with your feedback.",
        confirmText: "Got it",
      });
    } catch (error) {
      if (isReportGenerationRequestAborted(error)) {
        setSubmitError("");
        setSubmitMessage("Report generation was cancelled.");
        return;
      }
      console.error("Error generating project report:", error);
      setSubmitError(
        `Failed to generate project report: ${error?.message || "Unknown error"}`
      );
    } finally {
      clearReportGenerationAbort(abortController);
      setIsGeneratingProjectReport(false);
      setReportGenerationInProgress(false);
      onGeneratingChange?.(false);
    }
  };

  const handleCustomizeRequest = (report) => {
    if (canCustomizeSavedReport(report)) {
      setCustomizeTarget(report);
      return;
    }
    showAlert({
      type: "warning",
      title: "Cannot customize",
      message:
        customizeBlockedMessage(report) ||
        "This report cannot be customized.",
      confirmText: "OK",
    });
  };

  if (!project) {
    return (
      <div className="sme-tracking-state">
        <p className="sme-tracking-state__title">No Project Selected</p>
        <p className="sme-tracking-state__hint">
          Please select a project to create a report.
        </p>
      </div>
    );
  }

  return (
    <div className="sme-tracking-report">
      <div className="sme-tracking-report-card sme-report-compose">
        <div className="sme-tracking-report-card__header sme-report-compose__header">
          <div>
            <h3 className="sme-tracking-report-card__title app-heading-accent">
              Create report
            </h3>
            <p className="sme-report-compose__lede m-0">
              Generate reports with AI, attach a PDF or Word file, save drafts, then submit when
              the client should see the report.
            </p>
          </div>
        </div>

        <div className="sme-tracking-report-card__body">
          <Textarea
            id="remarks"
            ref={remarksInputRef}
            label="Remarks (optional)"
            rows={6}
            placeholder="Enter your remarks here..."
            value={remarks}
            onChange={handleRemarksChange}
            disabled={isSubmitting}
            maxLength={1000}
            className="sme-tracking-report-textarea"
          />
          <p className="sme-tracking-report-field__hint sme-tracking-report-field__hint--after-field">
            {remarks.length}/1000 characters
          </p>

          <div className="sme-tracking-report-field">
            <label htmlFor="fileUpload" className="sme-tracking-report-field__label">
              Attach PDF or Word (optional)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              id="fileUpload"
              className="sme-tracking-report-file-input"
              onChange={handleFileSelect}
              disabled={isSubmitting || isGeneratingProjectReport}
              accept=".pdf,.docx"
            />
            {selectedFiles[0] ? (
              <p className="sme-tracking-report-file-chosen">
                Selected: <strong>{selectedFiles[0].name}</strong>
              </p>
            ) : null}
            <p className="sme-tracking-report-field__hint">
              PDF or Word (.docx). Maximum file size: 10MB.
              Generate attaches an AI PDF, or a Word file when editable is on.
            </p>
          </div>

          {selectedFiles.length > 0 && (
            <div className="sme-tracking-report-field">
              <span className="sme-tracking-report-field__label">Selected file</span>
              <div className="sme-tracking-report-files">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="sme-tracking-report-file-row">
                    <div className="sme-tracking-report-file-row__info">
                      <span
                        className="sme-tracking-report-file-row__name"
                        title={file.name}
                      >
                        {file.name}
                      </span>
                      <span className="sme-tracking-report-file-row__size">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <div className="sme-tracking-report-file-row__actions">
                      {/\.pdf$/i.test(file.name) ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          onClick={() =>
                            setLocalPdfPreview({
                              file,
                              fileName: file.name,
                            })
                          }
                          disabled={isSubmitting}
                        >
                          Preview
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isSubmitting}
                        aria-label={`Remove ${file.name}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="sme-tracking-report-actions sme-report-compose__actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowTickerModal(true)}
              disabled={isGeneratingProjectReport || isSubmitting}
            >
              Generate report with AI
            </Button>

            {companyReportSelection ? (
              <p className="sme-tracking-report-field__hint m-0 w-full">
                Company: <strong>{companyReportSelection.ticker}</strong>
                {companyReportSelection.includeGeneralUseCases ? " · General use cases" : ""}
                {companyReportSelection.includeCompetitorUseCases
                  ? ` · Competitor use cases (${companyReportSelection.competitorTiers.join(", ")})`
                  : ""}
                {companyReportSelection.includeComparableEngagements
                  ? " · Comparable engagements"
                  : ""}
              </p>
            ) : null}

            <Button
              type="button"
              variant="secondary"
              onClick={handleSaveReport}
              disabled={
                isSavingReport ||
                isSubmitting ||
                isGeneratingProjectReport ||
                !projectId ||
                !clientUserId
              }
              isLoading={isSavingReport}
            >
              {isSavingReport ? "Saving…" : "Save report"}
            </Button>

            <Button
              type="button"
              variant="primary"
              onClick={handleSubmitReport}
              disabled={
                isSubmitting ||
                isSavingReport ||
                isGeneratingProjectReport ||
                !projectId ||
                !clientUserId
              }
              isLoading={isSubmitting}
            >
              {isSubmitting ? "Submitting…" : "Submit report"}
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={handleClearForm}
              disabled={
                isSubmitting || isSavingReport || isGeneratingProjectReport
              }
            >
              Clear form
            </Button>
          </div>

          {isGeneratingProjectReport && (
            <div
              className="sme-tracking-report-generating"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <Spinner size="md" className="sme-tracking-report-generating__spinner" />
              <div className="sme-tracking-report-generating__copy">
                <p className="sme-tracking-report-generating__title">
                  Generating AI report…
                  <span className="sme-tracking-report-generating__timer">
                    {formatCountdown(generateRemainingSec)}
                  </span>
                </p>
                <p className="sme-tracking-report-generating__hint">
                  {generationStatusHint}
                </p>
                <p className="sme-tracking-report-generating__warning">
                  This can take up to 10 minutes. Stay on this page until it finishes. If you
                  leave, generation is cancelled immediately (our API and AI services stop
                  processing).
                </p>
              </div>
            </div>
          )}

          {submitMessage ? (
            <Alert variant="success" description={submitMessage} className="sme-tracking-report-alert" />
          ) : null}

          {submitError ? (
            <Alert variant="error" description={submitError} className="sme-tracking-report-alert" />
          ) : null}
        </div>
      </div>

      <SmeProjectReportHistory
        projectId={projectId}
        clientUserId={clientUserId}
        reloadToken={reportListReloadToken}
        onCustomizeReport={handleCustomizeRequest}
        onReportsChanged={() => {
          void loadSubmittedReports();
          setReportListReloadToken((value) => value + 1);
        }}
      />

      <ReferenceCompanyTickerModal
        open={showTickerModal}
        onClose={() => setShowTickerModal(false)}
        initialQuery={companyNameForSearch}
        onComplete={setCompanyReportSelection}
        onGenerate={handleGenerateProjectReport}
      />

      {customizeTarget && projectId ? (
        <ReportCustomizeModal
          open={Boolean(customizeTarget)}
          onClose={() => setCustomizeTarget(null)}
          projectId={Number(projectId)}
          clientUserId={clientUserId}
          report={{
            reportItemId: customizeTarget.reportItemId,
            remarks: customizeTarget.remarks,
            sourceHtmlUrl: customizeTarget.sourceHtmlUrl,
            status: customizeTarget.status,
          }}
          onDraftSaved={() => {
            void loadSubmittedReports();
            setReportListReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      <PdfReportPreviewModal
        show={Boolean(localPdfPreview)}
        onHide={() => setLocalPdfPreview(null)}
        title="Capability Assessment Report"
        file={localPdfPreview?.file ?? null}
        fileName={localPdfPreview?.fileName || "report.pdf"}
      />
    </div>
  );
}
